import React from "react";
import PropTypes from "prop-types";
import { Chart } from "react-google-charts";
import LoadingAnimation from "../loadingAnimation/loadingAnimation";
import { lineToolTipFormatter, getTableHeight, getOptions, getContent } from "./functions";
import { xlsxExport } from "../../utils/xlsxExport";
import "./googleChart.css";

export const commonOptions = {
  titleTextStyle: {
    color: "#8791a0",
    fontSize: 15,
    fontWeight: 700
  },
  sliceVisibilityThreshold: 0,
  backgroundColor: { fill: "transparent" },
  pieSliceBorderColor: "transparent",
  tooltip: {
    isHtml: true,
    trigger: "selection",
    textStyle: { fontSize: 14 }
  },
  chartArea: { left: 0, width: "80%", height: "80%", bottom: 10 },
  legend: {
    alignment: "center",
    textStyle: {
      color: "#8791A0",
      fontSize: 16,
      bold: true
    },
    pagingTextStyle: {
      color: "#8fc447"
    },
    scrollArrows: {
      activeColor: "#8fc447"
    }
  },
  colors: ["#ff664e", "#1794ca", "#21e981", "#df30e5", "#1cdac4", "#7d4eff", "#ffbe4e", "#ca5317"]
};

let rowTriggeredByEventListner = null;

const GoogleStackedBarChart = (props) => {
  const {
    chartName = "",
    switchView,
    chartType,
    rootWrapperId = "",
    isTableView,
    width,
    position,
    enableEventListeners,
    highlightLastRow,
    enableLineChartTooltip,
    showCurrency = true,
    currencySymbol = "$",
    showAdditionTooltip = true,
    showXLSX = true,
    addColor = false,
    XLSXText = ""
  } = props;
  // const [csvContent, setCsvContent] = React.useState();
  const [content, setContent] = React.useState();
  const [chartImageURI, setChartImageURI] = React.useState();
  const [chartRows, setChartRows] = React.useState([]);
  const [chartColumns, setChartColumns] = React.useState([]);

  const height = isTableView ? getTableHeight(props.rows, props.data, props.height) : props.height;
  const options = getOptions(height, position, props.options);
  const fileName = chartName.replace(/_/g, "-");

  const handleSwitchView = (e) => {
    e.preventDefault();
    switchView(!isTableView);
  };

  React.useEffect(() => {
    if (enableLineChartTooltip) {
      const toolTip = lineToolTipFormatter(props.rows, props.columns, options.series);
      setChartRows(toolTip.rows);
      setChartColumns(toolTip.columns);
    }

    setChartRows(props.rows);
    setChartColumns(props.columns);
  }, []);

  const chartEvents = [
    {
      eventName: "ready",
      callback: ({ chartWrapper, google }) => {
        if (addColor === true) {
          const addColor = document.querySelector(
            rootWrapperId ? `#${rootWrapperId} .add-color` : ".add-color"
          );
          const tableNumbers = addColor.querySelectorAll(".google-visualization-table-type-number");
          for (const el of tableNumbers) {
            const num = el.textContent;

            if (num) {
              if (num.includes("-")) {
                el.classList.add("negative");
              } else if (num.startsWith("0")) {
                el.classList.add("zero");
              } else if (+num > 0) {
                el.classList.add("positive");
              }
            }
          }
        }
        if (!enableEventListeners || content || chartImageURI) {
          return;
        }
        const chart = chartWrapper.getChart();
        const dataTable = chartWrapper.getDataTable();

        if (!content && showXLSX) {
          const content = getContent(dataTable, isTableView);
          setContent(content);
        }
        if (!isTableView && !chartImageURI) {
          setChartImageURI(chart.getImageURI());
        }

        if (!showAdditionTooltip) return;
        google.visualization.events.addListener(chart, "onmouseover", (event) => {
          const selection = chart.getSelection();
          const { row } = event;
          if (
            row != null &&
            rowTriggeredByEventListner !== row &&
            rowTriggeredByEventListner === null
          ) {
            rowTriggeredByEventListner = row;
            const totalColumns = dataTable.getNumberOfColumns();
            const totalRows = dataTable.getNumberOfRows();
            let total = 0;
            let annotationIndex = 0;
            for (let i = 0; i < totalColumns; i++) {
              if (dataTable.getColumnRole(i) === "data") {
                total += dataTable.getValue(row, i);
              }
              if (dataTable.getColumnRole(i) === "annotation") {
                annotationIndex = i;
              }
            }

            showCurrency
              ? (total = total.formatMoney(2, currencySymbol))
              : (total = total.formatMoney(0, ""));

            if (dataTable.getValue(row, annotationIndex) !== total) {
              for (let i = 0; i < totalRows; i++) {
                if (i !== row) {
                  dataTable.setValue(i, annotationIndex, null);
                }
              }
              dataTable.setValue(row, annotationIndex, total);
              chart.draw(dataTable, options);
              if (!(selection.length === 0)) {
                chart.setSelection(selection);
              }
            }
          }
        });

        google.visualization.events.addListener(chart, "onmouseout", (event) => {
          if (chart.getSelection()[0] === undefined) {
            const row = event["row"];
            if (row != null && rowTriggeredByEventListner !== null) {
              rowTriggeredByEventListner = null;
              const totalColumns = dataTable.getNumberOfColumns();
              for (let i = 0; i < totalColumns; i++) {
                if (dataTable.getColumnRole(i) === "annotation") {
                  dataTable.setValue(row, i, null);
                }
              }
              chart.draw(dataTable, options);
            }
          }
        });
      }
    }
  ];

  return (
    <div
      className={`google-table-or-stacked-bar-chart-root ${
        highlightLastRow ? "highlight-last-row" : ""
      }`}
      data-testid="google-chart"
    >
      {enableEventListeners && (
        <div data-testid="addons-container" className="relative-50">
          <div className="chart-addons-container">
            {!isTableView && (
              <div className="chart-addons-control-item" id={`${chartName}_download_png`}>
                <a
                  title="Export to PNG"
                  download={`${fileName}.png`}
                  className="export_to_png"
                  href={chartImageURI}
                >
                  <span className="glyphicon glyphicon-picture" />
                </a>
              </div>
            )}
            {content && (
              <div
                className="chart-addons-control-item"
                id={`${chartName}_download_csv`}
                style={{ cursor: "pointer", color: "#8fc447" }}
                onClick={() => {
                  xlsxExport({
                    data: content.data,
                    headers: content.headers,
                    columns: props.columns,
                    fileName: "raw_data"
                  });
                }}
              >
                <a title="Export to Excel" className="export_to_csv" style={{ color: "#8fc447" }}>
                  <span className="glyphicon glyphicon-download-alt" />
                </a>
                {XLSXText.length > 0 ? <span className="xlsx-text title">{XLSXText}</span> : ""}
              </div>
            )}

            {/*{csvContent && (*/}
            {/*  <div className="chart-addons-control-item" id={`${chartName}_download_csv`}>*/}
            {/*    <a title={`Export ${readableChartName} to CSV`} className="export_to_csv" download={`${fileName}.csv`} href={csvContent}>*/}
            {/*      <span className="glyphicon glyphicon-download-alt" />*/}
            {/*    </a>*/}
            {/*  </div>*/}
            {/*)}*/}
            {typeof switchView === "function" && (
              <div className="chart-addons-control-item" id={`${chartName}_table`}>
                <a
                  className="switch_chart_type"
                  title={`Switch to ${isTableView ? "Chart" : "Table"} View`}
                  data-chart-name={chartName}
                  href="#"
                  onClick={handleSwitchView}
                >
                  <span
                    className={`glyphicon glyphicon-${isTableView ? "equalizer" : "th-list"}`}
                  />
                </a>
              </div>
            )}
          </div>
        </div>
      )}
      <Chart
        width={width}
        height={height}
        chartType={chartType || (isTableView ? "Table" : "ColumnChart")}
        rows={chartRows}
        columns={chartColumns}
        data={props.data}
        loader={<LoadingAnimation text="Loading Chart" />}
        chartEvents={chartEvents}
        options={options}
      />
    </div>
  );
};

GoogleStackedBarChart.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  chartName: PropTypes.string,
  isTableView: PropTypes.bool,
  switchView: PropTypes.func,
  enableEventListeners: PropTypes.bool,
  enableLineChartTooltip: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  position: PropTypes.string,
  options: PropTypes.object,
  highlightLastRow: PropTypes.bool,
  data: PropTypes.array,
  chartType: PropTypes.string,
  showCurrency: PropTypes.bool,
  showAdditionTooltip: PropTypes.bool,
  showXLSX: PropTypes.bool,
  addColor: PropTypes.bool,
  XLSXText: PropTypes.string
};

GoogleStackedBarChart.defaultProps = {
  // height: "400px",
  position: "right",
  enableEventListeners: true,
  enableLineChartTooltip: false,
  switchView: null,
  options: {},
  highlightLastRow: false,
  data: null,
  isTableView: false,
  chartType: ""
};

GoogleStackedBarChart.defaultProps = {
  series: []
};

export default GoogleStackedBarChart;
